import get from 'lodash/get'
import snakeCase from 'lodash/snakeCase'
import { default as deserialize, formatMeta } from 'app/utils/deserialize'
import FormErrors from 'app/utils/form_errors'
import { fetch, create, update, remove } from 'app/utils/data'
import { ADD_ON_RESOURCE_KEYS } from 'app/views/accounts/addOns/addOnResources'

// Normalize an API error into either a FormErrors instance (when the
// server returned a JSON:API `errors` array we can render inline) or
// re-throws the raw transport error (network failure, 5xx without a body,
// etc.) so callers see a real Error object with a stack trace.
function handleApiError(error) {
  if (error.response && error.response.data && error.response.data.errors) {
    throw new FormErrors(error.response.data.errors)
  }
  throw error
}

// Flattens the view's cart into the batch endpoint's wire shape. Keys stay
// snake_case because they travel as a nested object (see `commitAddOnCart`).
function serializeAddOnCart(cart) {
  const addOns = []
  const reductions = []

  ADD_ON_RESOURCE_KEYS.forEach((resource) => {
    const line = cart[resource]
    if (!line) return

    if (line.classification === 'add' && line.packDelta > 0) {
      addOns.push({ resource, quantity: line.packDelta })
    } else if (line.classification === 'reduce') {
      // A reduction names the number of packs that should still be billing
      // afterwards, not the ones being given up. The server splits a record when
      // the target lands mid-record, and a target re-applied is a no-op — which
      // is what keeps a retry after a declined charge from reducing twice.
      reductions.push({ resource, target_quantity: line.targetPacks })
    }
  })

  return {
    seats: cart.seats && cart.seats.targetSeats != null ? { target_seats: cart.seats.targetSeats } : null,
    add_ons: addOns,
    reductions
  }
}

export default {
  namespaced: true,

  state: {
    item: null,
    prorationData: null
  },

  getters: {
    currentSubscription: (state) => state.item,
    prorationData: (state) => state.prorationData,
    hasActiveSubscription: (state) => state.item && 'active' === get(state.item, 'status'),
    // Pending scheduled plan change (feature 018-native-plan-changes).
    // Synthesized server-side from Stripe's upcoming-invoice API and serialized
    // on the subscription's `pendingChange` attribute. Returns `null` when no
    // change is queued — same shape consumed by SubscriptionSummary.vue,
    // PendingChangeBanner.vue, and the /plans pending-change rendering.
    pendingChange: (state) => get(state.item, 'pendingChange') || null,
    // Pending scheduled seat reduction (feature 019-manage-seats). Synthesized
    // server-side from Stripe's subscription schedule and serialized on the
    // subscription's `pendingSeatChange` attribute. Returns `null` when no
    // reduction is scheduled — consumed by PendingSeatChangeBanner, the Team-tab
    // seat-management surface, and the Seats section on /account/add-on.
    pendingSeatChange: (state) => get(state.item, 'pendingSeatChange') || null,
    // Whether the customer's current plan is on the six-SKU whitelist for the
    // native Manage Seats flow. Drives entry-button visibility on the Team tab.
    seatChangesAllowed: (state) => get(state.item, 'seatChangesAllowed') === true
  },

  mutations: {
    subscriptionFetched(state, { subscription }) {
      state.item = subscription
    },

    prorationDataFetched(state, data) {
      state.prorationData = data
    },

    resetState(state) {
      state.item = null
      state.prorationData = null
    },

    paymentMethodUpdated(state, { paymentMethod }) {
      const { item } = state

      if (item) {
        item.cardType = paymentMethod.cardType
        item.cardLast4 = paymentMethod.cardLast4
        item.cardExpYear = paymentMethod.cardExpYear
        item.cardExpMonth = paymentMethod.cardExpMonth
      }
    }
  },

  actions: {
    async fetchSubscription({ commit }) {
      try {
        const response = await fetch({ url: `/api/subscription` })

        const subscription = await deserialize(response.data)

        commit('subscriptionFetched', { subscription })

        return subscription
      } catch (error) {
        if (error.response.status === 404) {
          return
        } else {
          throw error
        }
      }
    },

    async cancelAtPeriodEnd({ commit, dispatch, state }, valueOrPayload) {
      // Accepts either a boolean (existing callers) or a payload object
      // { cancelAtPeriodEnd, reasonId, comment } from the in-flow reason
      // capture flow (spec 013-cancellation-reasons). Reason fields are
      // included in the PATCH body only when present and the cancellation
      // is being initiated (cancelAtPeriodEnd === true).
      const isObjectPayload = typeof valueOrPayload === 'object' && valueOrPayload !== null
      const cancelAtPeriodEnd = isObjectPayload ? valueOrPayload.cancelAtPeriodEnd : valueOrPayload
      const reasonId = isObjectPayload ? valueOrPayload.reasonId : null
      const comment = isObjectPayload ? valueOrPayload.comment : null

      const changes = { cancelAtPeriodEnd }
      if (cancelAtPeriodEnd === true && reasonId) {
        changes.cancellationReasonId = reasonId
        if (comment) {
          changes.cancellationComment = comment
        }
      }

      try {
        const response = await update({
          url: '/api/subscription',
          record: state.item,
          patch: true,
          changes
        })

        const subscription = await deserialize(response.data)

        commit('subscriptionFetched', { subscription })

        // After the customer cancels (cancelAtPeriodEnd: true), refetch the current account so the in-memory
        // requiresBillingCancellation` flag (driven by the new cancel_at_period_end value) updates
        // immediately. Without this, the deletion modal's billing-cancellation gate would continue to fire
        // from stale Vuex account state until the customer refreshed the page.
        if (cancelAtPeriodEnd === true) {
          await dispatch('sessions/fetchCurrentSession', { force: true }, { root: true })
        }

        return subscription
      } catch (error) {
        if (error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw error
        }
      }
    },

    async reactivateSubscription({ commit, dispatch, state }, meta = {}) {
      try {
        const response = await create({
          url: '/api/subscription/reactivate',
          record: state.item,
          data: {},
          meta: meta
        })

        const subscription = await deserialize(response.data)

        commit('subscriptionFetched', { subscription })

        await dispatch('sessions/fetchCurrentSession', { force: true }, { root: true })

        return subscription
      } catch (error) {
        if (error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw error
        }
      }
    },

    async addSeats({ commit, state, dispatch }, { seats }) {
      try {
        const previousTotal = state.item.total

        const response = await update({
          url: '/api/subscription/add-seats',
          patch: true,
          record: state.item,
          changes: {
            seats
          }
        })

        const subscription = await deserialize(response.data)
        const meta = formatMeta(get(response, ['data', 'meta'], {}))
        const totalDifference = subscription.total.amount - previousTotal.amount

        await dispatch('sessions/fetchCurrentSession', { force: true }, { root: true })

        const result = {
          subscription,
          addingSeats: seats
        }

        if (meta.invoicedImmediately) {
          result.charged = { ...previousTotal, amount: totalDifference }
        }

        return result
      } catch (error) {
        if (error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw error
        }
      }
    },

    async createSubscription({ commit, dispatch }, dataAndMeta) {
      try {
        const { sku, seats, email, processor, processorToken, recaptchaToken, paymentMethod, isBacker } = dataAndMeta

        const response = await create({
          url: isBacker ? '/api/subscription/backer' : '/api/subscription',
          type: 'subscription',
          data: {
            sku,
            seats,
            email
          },
          meta: {
            processor,
            processorToken,
            recaptchaToken,
            paymentMethod: snakeCase(paymentMethod)
          }
        })

        const subscription = await deserialize(response.data)

        commit('subscriptionFetched', { subscription })

        if (isBacker) {
          await dispatch('sessions/fetchCurrentSession', { force: true }, { root: true })
        }

        return subscription
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async upgradeSubscription({ commit, state, dispatch }, { seats, sku, processor, processorToken, recaptchaToken }) {
      try {
        const response = await update({
          url: '/api/subscription',
          record: state.item,
          patch: true,
          changes: {
            sku,
            seats
          },
          meta: {
            processor,
            processorToken,
            recaptchaToken
          }
        })

        const subscription = await deserialize(response.data)

        await dispatch('sessions/fetchCurrentSession', { force: true }, { root: true })

        return subscription
      } catch (error) {
        if (error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw error
        }
      }
    },

    async fetchProrationData({ commit, state }, { seats, sku }) {
      try {
        const response = await create({
          url: '/api/proration-data',
          record: state.item,
          data: {
            sku,
            seats,
            subscription_id: state.item.id
          }
        })

        const prorationData = await deserialize(response.data)

        commit('prorationDataFetched', prorationData)

        return prorationData
      } catch (error) {
        if (error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw error
        }
      }
    },

    // ========================================================================
    // Native plan-change feature (018-native-plan-changes)
    //
    // Three actions back /api/subscription/plan-change{,/preview}. Each
    // throws via `handleApiError` on failure — server JSON:API errors are
    // wrapped in a `FormErrors` instance (whose array lives at
    // `.jsonApiErrors`); transport failures re-throw the raw axios error.
    // ========================================================================

    async previewPlanChange({ state }, { targetPlanSlug, targetCycle, targetSeats, discountCode }) {
      try {
        const response = await create({
          url: '/api/subscription/plan-change/preview',
          type: 'planChangePreview',
          record: state.item,
          data: { targetPlanSlug, targetCycle, targetSeats, discountCode }
        })

        return await deserialize(response.data)
      } catch (error) {
        handleApiError(error)
      }
    },

    // `addOns` (a list of `{resource, quantity}` packs) is only accepted on a
    // SCHEDULED change: the packs go into the schedule's next phase, so they
    // start with the new plan and are billed on its first invoice, nothing
    // today. The server refuses them on a change that applies immediately —
    // those buy through `commitAddOnCart` once the new plan is live.
    //
    // Sent as a nested array, so the serializer leaves the inner `resource` /
    // `quantity` keys alone.
    async confirmPlanChange(
      { commit, dispatch, state },
      { targetPlanSlug, targetCycle, targetSeats, prorationDate, acknowledged, discountCode, addOns, idempotencyKey }
    ) {
      try {
        const response = await create({
          url: '/api/subscription/plan-change',
          type: 'planChange',
          record: state.item,
          data: {
            targetPlanSlug,
            targetCycle,
            targetSeats,
            prorationDate,
            acknowledged,
            discountCode,
            add_ons: addOns && addOns.length ? addOns : undefined
          },
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined
        })

        const subscription = await deserialize(response.data)
        commit('subscriptionFetched', { subscription })

        // Pro+ access gates read from the permits store, which is fetched once
        // at page load. Refresh after a plan change so the new entitlement is
        // visible without a hard refresh.
        await dispatch('permits/fetchPermits', null, { root: true })

        // A prorated upgrade buys its packs server-side, right after the plan
        // swap and on an invoice of their own — Stripe has already raised and
        // settled the proration invoice by then. That leg can fail without
        // failing the change the customer has already been charged for, so the
        // commit reports it in `meta` rather than as an error, and the caller
        // has to say the plan landed and the capacity didn't.
        const meta = formatMeta(get(response, ['data', 'meta'], {}))

        return { subscription, addOnsFailed: meta.addOnsFailed || null }
      } catch (error) {
        handleApiError(error)
      }
    },

    async cancelPendingChange({ commit }) {
      try {
        const response = await remove({ url: '/api/subscription/plan-change' })

        const subscription = await deserialize(response.data)
        commit('subscriptionFetched', { subscription })

        return subscription
      } catch (error) {
        handleApiError(error)
      }
    },

    // ========================================================================
    // Native Manage Seats flow (feature 019-manage-seats)
    //
    // Three actions back /api/subscription/seat-change{,/preview}. Same error
    // handling shape as the plan-change actions above. No proration in either
    // direction — adds charge the full per-seat price immediately; reductions
    // are scheduled at renewal with no refund.
    // ========================================================================

    async previewSeatChange({ state }, { targetSeats }) {
      try {
        const response = await create({
          url: '/api/subscription/seat-change/preview',
          type: 'seatChangePreview',
          record: state.item,
          data: { targetSeats }
        })

        return await deserialize(response.data)
      } catch (error) {
        handleApiError(error)
      }
    },

    async confirmSeatChange({ commit, state }, { targetSeats, acknowledged, idempotencyKey }) {
      try {
        const response = await create({
          url: '/api/subscription/seat-change',
          type: 'seatChange',
          record: state.item,
          data: { targetSeats, acknowledged },
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined
        })

        const subscription = await deserialize(response.data)
        commit('subscriptionFetched', { subscription })

        return subscription
      } catch (error) {
        handleApiError(error)
      }
    },

    async cancelPendingSeatChange({ commit }) {
      try {
        const response = await remove({ url: '/api/subscription/seat-change' })

        const subscription = await deserialize(response.data)
        commit('subscriptionFetched', { subscription })

        return subscription
      } catch (error) {
        handleApiError(error)
      }
    },

    // ========================================================================
    // Subscription add-ons (feature 025-subscription-addons)
    //
    // Two actions back /api/subscription/add-on{,/preview}. Same error-handling
    // shape as the plan-change / seat-change actions (server JSON:API errors →
    // FormErrors; transport errors re-thrown). Preview is local math only (no
    // Stripe call). Confirm adds the recurring item to the Stripe subscription,
    // charges "due today" ($0 on monthly plans), and persists the local add-on.
    // ========================================================================

    // `targetPlanSlug` / `targetCycle` price the packs against a plan the
    // customer is MOVING TO (the plan-change flow's add-on step) rather than the
    // one they hold — without them a Max customer moving to Team is refused a
    // price for capacity Team's finite limits genuinely need. Preview only; the
    // commit is always made against the live plan.
    async previewAddOn({ state }, { resource, quantity, targetPlanSlug, targetCycle }) {
      const target = targetPlanSlug && targetCycle ? { targetPlanSlug, targetCycle } : {}

      try {
        const response = await create({
          url: '/api/subscription/add-on/preview',
          type: 'addOnPreview',
          record: state.item,
          data: { resource, quantity, ...target }
        })

        return await deserialize(response.data)
      } catch (error) {
        handleApiError(error)
      }
    },

    // `quantity` is the number of packs being ADDED, not the resulting total: a
    // purchase appends a Stripe item and an `add_ons` record, and the entitlement
    // seam sums the records.
    //
    // `refresh: false` skips the post-commit reload — the batched cart checkout
    // sets it so N sections don't trigger 2N refetches, and refreshes once itself.
    async confirmAddOn({ dispatch, state }, { resource, quantity, idempotencyKey, refresh = true }) {
      try {
        const response = await create({
          url: '/api/subscription/add-on',
          type: 'addOn',
          record: state.item,
          data: { resource, quantity },
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined
        })

        const addOn = await deserialize(response.data)

        if (refresh) await dispatch('refreshAddOnEntitlements')

        return addOn
      } catch (error) {
        handleApiError(error)
      }
    },

    // Reload the subscription (so `addOns` and `effectiveLimits` are current) and
    // the permits store (kit / icon-upload limit gates are fetched once at page
    // load), so a capacity change is visible without a reload.
    async refreshAddOnEntitlements({ dispatch }) {
      await dispatch('fetchSubscription')
      await dispatch('permits/fetchPermits', null, { root: true })
    },

    // Schedules an add-on's removal at period end (DELETE /api/subscription/add-on/:id).
    // $0 today, no refund; entitlement holds until the period boundary. Refreshes
    // the subscription so the "Ends on …" state shows.
    async cancelAddOn({ dispatch }, { id, refresh = true }) {
      try {
        const response = await remove({ url: `/api/subscription/add-on/${id}` })

        const addOn = await deserialize(response.data)

        if (refresh) await dispatch('refreshAddOnEntitlements')

        return addOn
      } catch (error) {
        handleApiError(error)
      }
    },

    // Cancels a scheduled removal (POST /api/subscription/add-on/:id/restore),
    // putting the add-on's recurring item back on the Stripe subscription. The
    // mirror of `cancelAddOn`, and $0 for the same reason: scheduling the
    // removal issued no refund, so restoring it owes nothing — the add-on simply
    // bills again at renewal. Refreshes so the "Ends on …" state clears.
    //
    // The add-on id travels in the path; the body carries nothing.
    async restoreAddOn({ dispatch }, { id, refresh = true }) {
      try {
        const response = await create({
          url: `/api/subscription/add-on/${id}/restore`,
          type: 'addOn',
          data: {}
        })

        const addOn = await deserialize(response.data)

        if (refresh) await dispatch('refreshAddOnEntitlements')

        return addOn
      } catch (error) {
        handleApiError(error)
      }
    },

    // Commit a combined add-ons cart (seats + any of the four add-on resources)
    // in one checkout (025-subscription-addons, D4).
    //
    // ONE request to the batch endpoint, which applies the whole cart in one
    // transaction and bills it on ONE itemized Stripe invoice. This used to loop
    // over the per-section endpoints instead: a customer buying seats, kits, and
    // pageviews together got three separate invoices for one checkout, and a
    // failure partway through left the earlier sections paid for with no unwind.
    //
    // `cart` shape: { seats: { targetSeats, preview } | null, kits: <AddOnPicker
    // selection> | null, icon_uploads: {...}, pageviews: {...}, bandwidth: {...} }.
    // A picker selection carries `classification` ('add' | 'reduce'), `packDelta`
    // (packs to buy) and `targetPacks` (packs that should still be billing after
    // a reduction).
    // Server-authoritative pricing for a whole cart. The combined `renewal` in
    // particular cannot be composed client-side: the seat preview reports the
    // WHOLE subscription's renewal while each add-on preview reports only its
    // own increment, so summing them double-counts the plan.
    //
    // Returns null for an empty cart rather than calling the API for nothing.
    async previewAddOnCart({ state }, { cart }) {
      const serialized = serializeAddOnCart(cart)
      if (!serialized.seats && !serialized.add_ons.length && !serialized.reductions.length) return null

      try {
        const response = await create({
          url: '/api/subscription/add-on/batch/preview',
          type: 'addOnCartPreview',
          record: state.item,
          data: { cart: serialized }
        })

        return await deserialize(response.data)
      } catch (error) {
        handleApiError(error)
      }
    },

    // `entryPoint` ('upsell' | 'plans_page') says where the purchase started, for
    // the add-on purchase count. It rides inside `cart` for the same reason the
    // cart's own keys do; left out, the server records the add-ons page.
    async commitAddOnCart({ dispatch, state }, { cart, entryPoint = null, idempotencyKey }) {
      try {
        const response = await create({
          url: '/api/subscription/add-on/batch',
          type: 'addOnCart',
          record: state.item,
          // Nested under one `cart` attribute on purpose: the JSON:API
          // serializer dasherizes top-level attribute NAMES (`add_ons` would go
          // out as `add-ons`) but leaves nested object keys untouched, so this
          // is the shape whose snake_case keys reach the server intact.
          data: { cart: { ...serializeAddOnCart(cart), ...(entryPoint ? { entry_point: entryPoint } : {}) } },
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined
        })

        const result = await deserialize(response.data)

        await dispatch('refreshAddOnEntitlements')

        return {
          addOns: result.addOns || [],
          removed: result.removed || [],
          chargedToday: result.chargedToday,
          invoiceId: result.invoiceId,
          // Non-empty only when the cart was charged but a trailing $0 scheduled
          // change (a seat reduction) didn't apply. The caller must say so
          // rather than report an unqualified success.
          failed: result.failed || []
        }
      } catch (error) {
        handleApiError(error)
      }
    }
  }
}
